import {TargetBlank} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {SortOption} from '../hooks/useSharedViewsItems';
import {WorkspacePreferences} from '../hooks/useWorkspacePreferences';
import {NamedWorkspaceMenuItem} from '../types';
import {WANDB_WORKSPACES_DOCS} from '../utils';
import {NamedWorkspaceMenuGroup} from './NamedWorkspaceMenuGroup';
import {NamedWorkspaceSortOptionSelector} from './NamedWorkspaceSortOptionSelector';

export const SAVED_VIEWS_INFO_CONTENT =
  'Views are workspaces saved by you or your teammates. Use views for future reference or easy collaborative analysis.';

type NamedWorkspaceSharedViewsProps = {
  sharedViewsItems: NamedWorkspaceMenuItem[];
  sortOption: SortOption;
  shouldShowMyViewsOnly: boolean;
  debouncedSearch: string;
  shouldShowInfo: boolean;
  setShouldShowInfo: (value: any) => void;
  updatePreference: (key: keyof WorkspacePreferences, value: any) => void;
};

export const NamedWorkspaceSharedViews = ({
  sharedViewsItems,
  sortOption,
  shouldShowMyViewsOnly,
  debouncedSearch,
  shouldShowInfo,
  setShouldShowInfo,
  updatePreference,
}: NamedWorkspaceSharedViewsProps) => {
  const sharedViewsCount = sharedViewsItems.length;

  return (
    <>
      <DropdownMenu.Label
        data-test="shared-view-label"
        className="mb-8 mt-16 flex flex-wrap items-center justify-between gap-16 px-12">
        <div className="flex gap-8 text-base font-semibold leading-[24px]">
          <p>Saved views</p>
          {debouncedSearch ||
          (shouldShowMyViewsOnly && sharedViewsCount > 0) ? (
            <div className="rounded bg-moon-500/[0.05] px-6 text-sm">
              {`${sharedViewsItems.length} of ${sharedViewsCount}`}
            </div>
          ) : (
            <div className="text-sm text-moon-500">{sharedViewsCount}</div>
          )}
        </div>
        <div
          className={twMerge(
            'flex cursor-pointer items-center justify-center rounded',
            'text-moon-500 '
          )}>
          <Button
            variant="ghost"
            icon="info"
            onClick={() => {
              setShouldShowInfo((prev: boolean) => !prev);
            }}
            className={twMerge(
              'h-32 w-32 p-8',
              'text-moon-800 dark:text-moon-300 dark:hover:text-moon-100'
            )}
          />
          <NamedWorkspaceSortOptionSelector
            selectedOption={sortOption}
            onSelectOption={e => updatePreference('sortOption', e)}
            shouldShowMyViewsOnly={shouldShowMyViewsOnly}
            onShowMyViewsOnly={value =>
              updatePreference('showMyViewsOnly', value)
            }
          />
        </div>
      </DropdownMenu.Label>
      {shouldShowInfo && (
        <div className="flex w-full justify-center">
          <p
            className={twMerge(
              'mx-12 mb-8 h-full w-full rounded px-12 py-8 text-sm',
              'bg-moon-50 text-moon-500 dark:bg-moonbeam/[.05]'
            )}>
            {SAVED_VIEWS_INFO_CONTENT}{' '}
            <span>
              <TargetBlank
                href={WANDB_WORKSPACES_DOCS}
                className="font-semibold text-teal-600 hover:text-teal-550">
                Learn more about views
              </TargetBlank>
            </span>{' '}
            in Docs.
          </p>
        </div>
      )}
      <NamedWorkspaceMenuGroup options={sharedViewsItems} defaultMaxItems={5} />
    </>
  );
};
